import { useState, useLayoutEffect } from "react";

/**
 * Custom hook to get the dimensions of the window or a referenced element.
 * @param {Integer} offsetHeight Offset to subtract from height (default = 0)
 * @param {Integer} offsetWidth Offset to subtract from width (default = 0)
 * @param {Object} ref React ref object pointing to an element (optional)
 * @returns {{ width: number, height: number }} Object containing width and height
 */
const useWindowResize = (offsetHeight = 0, offsetWidth = 0, ref = null) => {
  const [size, setSize] = useState(() => {
    if (ref?.current) {
      return {
        width: ref.current.clientWidth - offsetWidth,
        height: ref.current.clientHeight - offsetHeight,
      };
    }
    return {
      width: window.innerWidth - offsetWidth,
      height: window.innerHeight - offsetHeight,
    };
  });

  useLayoutEffect(() => {
    function updateSize() {
      if (ref?.current) {
        setSize({
          width: ref.current.clientWidth - offsetWidth,
          height: ref.current.clientHeight - offsetHeight,
        });
      } else {
        setSize({
          width: window.innerWidth - offsetWidth,
          height: window.innerHeight - offsetHeight,
        });
      }
    }

    window.addEventListener("resize", updateSize);
    updateSize();

    return () => window.removeEventListener("resize", updateSize);
  }, [ref, offsetHeight, offsetWidth]);

  return size;
};

export default useWindowResize;
